import { API_BASE } from '../../config/app';

export const SAMPLE_TEMPLATES = [
  {
    id: 101,
    name: 'Launch Newsletter',
    category: 'Industry',
    categoryKey: 'industry',
    subcategoryId: 'industry-newsletter',
    subcategory: 'Newsletter',
    previewImage: 'https://images.unsplash.com/photo-1497366754035-f200968a6e72?auto=format&fit=crop&w=900&q=80',
    html: sampleTemplateHtml('Launch Newsletter', 'Product updates, customer wins, and the next big launch in one clean newsletter.'),
  },
  {
    id: 102,
    name: 'Flash Sale Promo',
    category: 'Industry',
    categoryKey: 'industry',
    subcategoryId: 'industry-promotional',
    subcategory: 'Promotional',
    previewImage: 'https://images.unsplash.com/photo-1607083206968-13611e3d76db?auto=format&fit=crop&w=900&q=80',
    html: sampleTemplateHtml('Flash Sale Promo', 'A bold promotional email layout for limited-time offers and ecommerce campaigns.'),
  },
  {
    id: 103,
    name: 'Welcome Journey',
    category: 'Lifecycle',
    categoryKey: 'lifecycle',
    subcategoryId: 'lifecycle-welcome-email',
    subcategory: 'Welcome Email',
    previewImage: 'https://images.unsplash.com/photo-1516321318423-f06f85e504b3?auto=format&fit=crop&w=900&q=80',
    html: sampleTemplateHtml('Welcome Journey', 'Give new subscribers a warm first impression with a polished onboarding email.'),
  },
  {
    id: 104,
    name: 'Product Drop',
    category: 'Industry',
    categoryKey: 'industry',
    subcategoryId: 'industry-ecommerce',
    subcategory: 'E-commerce',
    previewImage: 'https://images.unsplash.com/photo-1542291026-7eec264c27ff?auto=format&fit=crop&w=900&q=80',
    html: sampleTemplateHtml('Product Drop', 'Showcase a new product with a clean hero, benefit blocks, and a strong call to action.'),
  },
  {
    id: 105,
    name: 'Automation Follow-up',
    category: 'Lifecycle',
    categoryKey: 'lifecycle',
    subcategoryId: 'lifecycle-automation',
    subcategory: 'Automation',
    previewImage: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?auto=format&fit=crop&w=900&q=80',
    html: sampleTemplateHtml('Automation Follow-up', 'A flexible follow-up template for nurturing leads after signups, purchases, or demos.'),
  },
  {
    id: 106,
    name: 'Company Announcement',
    category: 'Lifecycle',
    categoryKey: 'lifecycle',
    subcategoryId: 'lifecycle-announcement',
    subcategory: 'Announcement',
    previewImage: 'https://images.unsplash.com/photo-1500530855697-b586d89ba3ee?auto=format&fit=crop&w=900&q=80',
    html: sampleTemplateHtml('Company Announcement', 'Share important news with a simple, professional announcement email.'),
  },
];

export function slugify(value) {
  return String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

export function templatePath(template) {
  const id = template?.id;
  const slug = slugify(template?.name);
  return `/templates/${slug ? `${slug}-` : ''}${id}`;
}

export function templateIdFromParam(value) {
  const param = String(value || '');
  const match = param.match(/(\d+)$/);
  return match ? match[1] : param;
}

export function templateSeoCopy(template) {
  const name = template?.name || 'Email Template';
  const category = template?.subcategory || template?.category || 'marketing';
  const words = slugify(name).split('-').filter(Boolean);
  const focusTerms = words.length ? words.join(', ') : 'email campaign';
  const campaignType = words.includes('cart')
    ? 'cart recovery and ecommerce follow-up'
    : words.includes('sale') || words.includes('deals') || words.includes('offer')
      ? 'promotional sales'
      : words.includes('welcome')
        ? 'subscriber onboarding'
        : words.includes('shipping') || words.includes('order')
          ? 'transactional customer updates'
          : `${category.toLowerCase()} campaign`;

  return {
    intro: `${name} is a free ${category.toLowerCase()} email template built for ${campaignType}. Use it when you need a responsive campaign layout that can be customized with your own brand colors, product images, copy, links, and call to action.`,
    details: `This template page is focused on the ${focusTerms} campaign theme, so marketers can preview the design, understand the best use case, and decide whether it fits a newsletter, promotion, announcement, lifecycle message, or customer update.`,
    checklist: [
      `Responsive HTML email layout for ${campaignType}`,
      `Editable sections for headlines, images, body copy, and buttons`,
      `Suitable for Mailsnappy campaigns, template editing, and HTML download workflows`,
    ],
  };
}

export function resolvePreviewImage(template) {
  const image = template.preview_url || template.preview_image || template.image || template.thumbnail;
  if (!image) return '';
  const root = API_BASE.replace(/\/api\/?$/, '');
  if (String(image).includes('yourdomain.com')) {
    const path = String(image).replace(/^https?:\/\/yourdomain\.com/i, '');
    return `${root}${path.startsWith('/') ? path : `/${path}`}`;
  }
  if (/^https?:\/\//.test(image) || image.startsWith('data:')) return image;
  if (image.startsWith('/storage/')) return `${root}${image}`;
  return `${root}/storage/template_previews/${image}`;
}

export function normalizeTemplate(template) {
  const categoryName = template.category?.name || template.category_name || template.category || 'Template';
  const categoryId = template.category_id || template.category?.id || slugify(categoryName);
  const subcategoryName = template.subcategory?.name || template.subcategory_name || '';
  return {
    id: template.id,
    name: template.name || template.title || 'Untitled template',
    category: categoryName,
    categoryId,
    categoryKey: slugify(categoryName),
    // Templates are assigned to a subcategory; category_id is its parent.
    subcategoryId: template.subcategory_id || template.subcategory?.id || null,
    subcategory: subcategoryName,
    html: template.html || '',
    description: template.description || template.summary || '',
    previewImage: resolvePreviewImage(template),
  };
}

export function normalizeCategory(category) {
  if (typeof category === 'string') return { id: slugify(category), name: category, subcategories: [] };
  return {
    id: category.id || slugify(category.name),
    name: category.name || 'Category',
    templatesCount: Number(category.templates_count || 0),
    subcategories: Array.isArray(category.subcategories)
      ? category.subcategories.map((sub) => ({
        id: sub.id,
        name: sub.name || 'Subcategory',
        parentId: sub.parent_id ?? category.id,
        templatesCount: Number(sub.templates_count || 0),
      }))
      : [],
  };
}

/**
 * Derive a category -> subcategory tree from the templates themselves. Used when
 * the categories endpoint is unavailable or the catalog is running on the bundled
 * sample data, so the sidebar still groups instead of collapsing to a flat list.
 */
export function buildCategoryTreeFromTemplates(templates) {
  const byCategory = new Map();

  templates.forEach((template) => {
    const categoryId = template.categoryId || template.categoryKey;
    if (!byCategory.has(String(categoryId))) {
      byCategory.set(String(categoryId), {
        id: categoryId,
        name: template.category,
        templatesCount: 0,
        subcategories: [],
      });
    }

    const category = byCategory.get(String(categoryId));
    category.templatesCount += 1;

    const subId = template.subcategoryId || `${categoryId}-${slugify(template.subcategory || template.category)}`;
    let sub = category.subcategories.find((item) => String(item.id) === String(subId));
    if (!sub) {
      sub = {
        id: subId,
        name: template.subcategory || template.category,
        parentId: categoryId,
        templatesCount: 0,
      };
      category.subcategories.push(sub);
    }
    sub.templatesCount += 1;
  });

  return Array.from(byCategory.values());
}

export function forceDesktopPreview(html) {
  if (!html) return '';
  const desktopMeta = '<meta name="viewport" content="width=760">';
  if (/<meta name="viewport"[^>]*>/i.test(html)) {
    return html.replace(/<meta name="viewport"[^>]*>/i, desktopMeta);
  }
  if (/<head[^>]*>/i.test(html)) {
    return html.replace(/<head[^>]*>/i, (match) => `${match}${desktopMeta}`);
  }
  return `${desktopMeta}${html}`;
}

function sampleTemplateHtml(name, description) {
  return `<!doctype html>
<html>
  <head>
    <meta charset="utf-8">
    <meta name="viewport" content="width=760">
    <title>${name}</title>
  </head>
  <body style="margin:0;background:#eef2ff;font-family:Arial,sans-serif;color:#111827;">
    <table role="presentation" width="100%" cellspacing="0" cellpadding="0" style="background:#eef2ff;padding:32px 0;">
      <tr>
        <td align="center">
          <table role="presentation" width="640" cellspacing="0" cellpadding="0" style="background:#ffffff;border-radius:18px;overflow:hidden;">
            <tr>
              <td style="background:#4f46e5;padding:38px 42px;color:#ffffff;">
                <p style="margin:0 0 10px;font-size:13px;font-weight:bold;letter-spacing:1px;text-transform:uppercase;">Mailsnappy template</p>
                <h1 style="margin:0;font-size:36px;line-height:1.1;">${name}</h1>
              </td>
            </tr>
            <tr>
              <td style="padding:34px 42px;">
                <p style="margin:0 0 22px;font-size:18px;line-height:1.7;color:#475569;">${description}</p>
                <a href="#" style="display:inline-block;background:#111827;color:#ffffff;text-decoration:none;border-radius:10px;padding:14px 22px;font-weight:bold;">Customize this template</a>
              </td>
            </tr>
            <tr>
              <td style="padding:0 42px 36px;">
                <table role="presentation" width="100%" cellspacing="0" cellpadding="0">
                  <tr>
                    <td style="padding:18px;background:#f8fafc;border-radius:14px;">
                      <h2 style="margin:0 0 8px;font-size:20px;">Built for fast campaigns</h2>
                      <p style="margin:0;color:#64748b;line-height:1.6;">Edit colors, text, sections, and links in the Mailsnappy editor, then export or send from your workflow.</p>
                    </td>
                  </tr>
                </table>
              </td>
            </tr>
          </table>
        </td>
      </tr>
    </table>
  </body>
</html>`;
}
